import React, { useEffect, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { ProductIcon, IconWithTransom } from './WindowIcons';
import { defaultColors } from '../config/productConfig';

/**
 * The frame (profile) of a drawing takes the colour chosen for the product.
 * Same rule as backend/frame_color.py, which does it for the PDF: keep the two in sync.
 */

export const frameHex = (colorId, colors = defaultColors) => {
  const c = (colors || []).find(x => x.id === colorId);
  return c && c.id !== 'none' && /^#[0-9a-fA-F]{6}$/.test(c.hex || '') ? c.hex : null;
};

// The outer frame of every icon is the first shape drawn with fill="none" and stroke-width="2.5":
// a filled copy of it, put behind everything, shows the colour between the frame and the glass
// (1.5 for the few thin-framed ones)
const FRAME_RES = ['2\\.5', '1\\.5'].map(w => new RegExp(`<(rect|path|polygon|circle|ellipse)\\b[^>]*\\bfill="none"[^>]*\\bstroke-width="${w}"[^>]*\\/?>`));

export const isDark = (hex) => {
  const [r, g, b] = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));
  return 0.299 * r + 0.587 * g + 0.114 * b < 140;
};

export function tintFrame(svg, hex) {
  if (!hex || !svg) return svg;
  const re = FRAME_RES.find(r => r.test(svg));
  const m = re && svg.match(re);
  if (!m || !/<svg\b[^>]*>/.test(svg)) return svg;
  const frame = m[0];
  const backdrop = frame
    .replace('fill="none"', `fill="${hex}"`)
    .replace(/\bstroke="[^"]*"/, 'stroke="none"')
    .replace(/\/?>$/, '/>');
  // Dark profiles: the frame line itself too (arches and portholes have no visible band)
  let out = isDark(hex)
    ? svg.slice(0, m.index) + frame.replace(/\bstroke="[^"]*"/, `stroke="${hex}"`) + svg.slice(m.index + frame.length)
    : svg;
  const open = out.match(/<svg\b[^>]*>/);
  const at = open.index + open[0].length;
  out = out.slice(0, at) + backdrop + out.slice(at);
  return out;
}

// Custom drawings (backend/custom_drawing.py) paint the frame with this fill
export const tintDrawing = (svg, hex) => (hex && svg ? svg.replace('fill="#F2F2F2"', `fill="${hex}"`) : svg);

/** Product icon (with sopraluce/sottoluce when given) whose frame has the product's colour. */
export function FramedIcon({ type, transomPosition, colorId, hex: hexProp, className }) {
  const hex = hexProp || frameHex(colorId);
  const element = transomPosition
    ? <IconWithTransom type={type} position={transomPosition} className="w-full h-full" />
    : <ProductIcon type={type} className="w-full h-full" />;
  // Built after render (never during it: a nested renderToStaticMarkup there breaks React's hooks)
  const [html, setHtml] = useState(null);
  useEffect(() => {
    setHtml(hex ? tintFrame(renderToStaticMarkup(element), hex) : null);
  }, [type, transomPosition, hex]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!html || !hex) return <div className={className}>{element}</div>;
  // Markup produced by our own icon components (no user input)
  return <div className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}
